/**
 * Radar Tile Recoloring
 * Maps NWS conus_bref_qcd tile colors to rain color palette.
 *
 * Provides both sync (fallback) and async (worker) APIs:
 * - recolorTile(): Sync, for fallback when worker unavailable
 * - recolorTileAsync(): Async, uses Web Worker for off-thread processing
 */

import { buildColorLUT, rgbToHsv, hsvToRampIndex } from "./radarColorLUT";
import { RAIN_RAMP } from "./radarColorRamps";

// ============================================================================
// Worker Management
// ============================================================================

let worker: Worker | null = null;
let workerReady = false;
let workerFailed = false;
let initialization: Promise<void> | null = null;
let requestId = 0;
let workersCreated = 0;
interface PendingRequest {
  complete: (data?: ImageData, error?: Error) => void;
}
const pending = new Map<number, PendingRequest>();
export function getRecolorDiagnostics() {
  return { workersCreated, pending: pending.size, ready: workerReady };
}

/** One worker is shared for the application lifetime, including overlay rebuilds. */
export function initRecolorWorker(): Promise<void> {
  if (initialization) return initialization;
  if (workerFailed || workerReady) return Promise.resolve();
  initialization = new Promise<void>((resolve) => {
    let candidate: Worker | null = null;
    let timeout: ReturnType<typeof setTimeout>;
    const fail = () => {
      clearTimeout(timeout);
      candidate?.terminate();
      worker = null;
      workerReady = false;
      workerFailed = true;
      for (const request of pending.values())
        request.complete(undefined, new Error("Radar worker unavailable"));
      resolve();
    };
    try {
      candidate = new Worker(
        new URL("./recolorRadarTile.worker.ts", import.meta.url),
        { type: "module" },
      );
      worker = candidate;
      workersCreated++;
      timeout = setTimeout(fail, 5000);
      candidate.onerror = fail;
      candidate.onmessage = (event: MessageEvent) => {
        if (event.data.type === "ready") {
          clearTimeout(timeout);
          workerReady = true;
          resolve();
        } else {
          const { id, type, imageData, error } = event.data;
          if (type === "done") pending.get(id)?.complete(imageData);
          else if (type === "error")
            pending
              .get(id)
              ?.complete(undefined, new Error(error || "Radar worker error"));
        }
      };
      const lut = buildColorLUT();
      candidate.postMessage({ type: "init", lut }, [lut.buffer]);
    } catch {
      fail();
    }
  });
  return initialization;
}

export function isWorkerReady(): boolean {
  return workerReady && worker !== null;
}

/** Released tiles drop their pending request and canvas immediately. */
export async function recolorTileAsync(
  img: HTMLImageElement,
  ownerDocument: Document,
  signal?: AbortSignal,
): Promise<HTMLCanvasElement> {
  signal?.throwIfAborted();
  if (!isWorkerReady()) return recolorTile(img, ownerDocument);
  const canvas = ownerDocument.createElement("canvas");
  canvas.width = canvas.height = 256;
  const ctx = canvas.getContext("2d")!;
  const id = ++requestId;
  return new Promise((resolve, reject) => {
    const complete = (data?: ImageData, error?: Error) => {
      if (!pending.delete(id)) return;
      clearTimeout(timeout);
      signal?.removeEventListener("abort", abort);
      if (error) {
        canvas.width = canvas.height = 0;
        reject(error);
        return;
      }
      try {
        ctx.putImageData(data!, 0, 0);
        resolve(canvas);
      } catch (error) {
        canvas.width = canvas.height = 0;
        reject(error);
      }
    };
    const abort = () =>
      complete(undefined, new DOMException("Tile released", "AbortError"));
    const timeout = setTimeout(
      () => complete(undefined, new Error("Radar worker timeout")),
      5000,
    );
    pending.set(id, { complete });
    signal?.addEventListener("abort", abort, { once: true });
    try {
      ctx.drawImage(img, 0, 0, 256, 256);
      const imageData = ctx.getImageData(0, 0, 256, 256);
      worker!.postMessage({ type: "recolor", id, imageData }, [
        imageData.data.buffer,
      ]);
    } catch (error) {
      complete(
        undefined,
        error instanceof Error ? error : new Error(String(error)),
      );
    }
  });
}

// ============================================================================
// Sync API (fallback)
// ============================================================================

/** Convert RAIN_RAMP Uint8Array to object array for sync recoloring */
function buildRainRampObjects(): Array<{ r: number; g: number; b: number }> {
  const result: Array<{ r: number; g: number; b: number }> = [];
  for (let i = 0; i < RAIN_RAMP.length; i += 3) {
    result.push({ r: RAIN_RAMP[i], g: RAIN_RAMP[i + 1], b: RAIN_RAMP[i + 2] });
  }
  return result;
}

const rainRampObjects = buildRainRampObjects();

/**
 * Recolor an NWS radar tile synchronously (fallback).
 * Use recolorTileAsync() when possible for better performance.
 */
export function recolorTile(
  img: HTMLImageElement,
  ownerDocument: Document,
): HTMLCanvasElement {
  const canvas = ownerDocument.createElement("canvas");
  canvas.width = 256;
  canvas.height = 256;
  const ctx = canvas.getContext("2d")!;

  ctx.drawImage(img, 0, 0, 256, 256);

  const imageData = ctx.getImageData(0, 0, 256, 256);
  const data = imageData.data;

  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] === 0) continue;

    const { h, s, v } = rgbToHsv(data[i], data[i + 1], data[i + 2]);
    const idx = hsvToRampIndex(h, s, v);

    if (idx >= 0) {
      const color = rainRampObjects[idx];
      data[i] = color.r;
      data[i + 1] = color.g;
      data[i + 2] = color.b;
    } else {
      data[i + 3] = 0;
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas;
}
